import { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import gsap from 'gsap';

interface Skill {
  name: string;
  level: number;
  color: string;
}

const skills: Skill[] = [
  { name: 'Python', level: 95, color: '#3776AB' },
  { name: 'JavaScript', level: 95, color: '#F7DF1E' },
  { name: 'Java', level: 90, color: '#007396' },
  { name: 'React', level: 90, color: '#61DAFB' },
  { name: 'Node.js', level: 85, color: '#339933' },
  { name: 'Django', level: 85, color: '#092E20' },
];

const technologies = [
  'Cloud Computing', 'Artificial Intelligence', 'Machine Learning', 'IoT',
  'Agile Development', 'DevOps', 'CI/CD', 'Git', 'AWS', 'PostgreSQL'
];

export default function Skills() {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
      skills.forEach((skill, index) => {
        gsap.to(`#skill-progress-${index}`, {
          width: `${skill.level}%`,
          duration: 1,
          ease: 'power2.out',
          delay: index * 0.2
        });
      });
    }
  }, [controls, inView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="skills" className="py-20 bg-gradient-to-br from-purple-800 via-gray-800 to-indigo-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Technical <span className="bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">Skills</span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-purple-300 to-indigo-300 mx-auto rounded-full"></div>
        </motion.div>

        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12"
        >
          <div className="space-y-6">
            <motion.h3
              variants={itemVariants}
              className="text-2xl font-semibold text-white mb-6"
            >
              Core Skills
            </motion.h3>
            {skills.map((skill, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="space-y-2"
              >
                <div className="flex justify-between items-center">
                  <span className="text-gray-200 font-medium">{skill.name}</span>
                  <span className="text-gray-200">{skill.level}%</span>
                </div>
                <div className="h-3 bg-gray-600 rounded-full overflow-hidden">
                  <div
                    id={`skill-progress-${index}`}
                    className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500"
                    style={{ width: '0%' }}
                  ></div>
                </div>
              </motion.div>
            ))}
          </div>

          <div>
            <motion.h3
              variants={itemVariants}
              className="text-2xl font-semibold text-white mb-6"
            >
              Technologies & Tools
            </motion.h3>
            <motion.div
              variants={containerVariants}
              className="grid grid-cols-2 sm:grid-cols-3 gap-4"
            >
              {technologies.map((tech, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="p-4 bg-gradient-to-br from-gray-700/50 to-purple-700/30 rounded-xl text-center shadow-lg border border-purple-400/20 hover:shadow-purple-400/20 transition-all duration-300 transform hover:scale-105 backdrop-blur-sm"
                >
                  <span className="text-gray-200 font-medium">{tech}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}